import type { Batch, HistoryEntry, Sample } from '@ctcells/contract'

export interface BatchSampleHistory {
  sampleIds: readonly string[]
  assignments: Batch['sample_assignments']
  incomplete: boolean
}

function object(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

export function batchSampleHistory(
  batch: Batch,
  samples: readonly Sample[],
  entries: readonly HistoryEntry[],
): BatchSampleHistory {
  const wells = new Map<string, Set<string>>()
  const counts = new Set<number>()
  let incomplete = false
  const add = (sampleId: string, well: string | null | undefined) => {
    const known = wells.get(sampleId) ?? new Set<string>()
    if (well) known.add(well)
    wells.set(sampleId, known)
  }
  for (const assignment of batch.sample_assignments) add(assignment.sample_id, assignment.wellName)
  for (const sample of samples) {
    if (sample.batch_id === batch.batch_id) add(sample.sample_id, sample.assigned_well)
  }
  for (const entry of entries) {
    if (entry.event_type !== 'audit' || entry.target !== `batch:${batch.batch_id}`) continue
    const detail = object(entry.detail)
    if (detail['operation'] === 'createBatch') {
      const count = detail['sample_count']
      if (typeof count === 'number' && Number.isInteger(count) && count >= 0) counts.add(count)
      else incomplete = true
    }
    if (detail['operation'] !== 'recoverBatch' || detail['decision'] !== 'rerun_samples') continue
    const before = object(detail['before'])
    const after = object(detail['after'])
    const sampleId = detail['sample_id']
    const well = before['assigned_well']
    if (
      typeof sampleId !== 'string' ||
      sampleId.trim() === '' ||
      before['batch_id'] !== batch.batch_id ||
      after['batch_id'] !== null ||
      after['assigned_well'] !== null ||
      (well !== null && (typeof well !== 'string' || well.trim() === ''))
    ) {
      incomplete = true
      continue
    }
    add(sampleId, well)
  }
  const assignments: Batch['sample_assignments'] = []
  const owners = new Map<string, string[]>()
  for (const [sampleId, assigned] of wells) {
    if (assigned.size > 1) incomplete = true
    for (const well of assigned) owners.set(well, [...(owners.get(well) ?? []), sampleId])
  }
  for (const [sampleId, assigned] of wells) {
    for (const wellName of assigned) {
      if (assigned.size === 1 && owners.get(wellName)?.length === 1)
        assignments.push({ sample_id: sampleId, wellName })
      else incomplete = true
    }
  }
  if (counts.size !== 1 || !counts.has(wells.size)) incomplete = true
  return { sampleIds: [...wells.keys()], assignments, incomplete }
}
